/**
 * Demo Routes Plugin
 *
 * 为 MPC 应用提供 Demo 路由热更新功能
 * 开发模式下自动生成 demo-routes.generated.ts 文件
 * 存放位置: features/mpc-common/src/devtools/demo-router
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  parseTagsString,
  validateAiTags,
  validateComplexity,
  parseUseCaseString,
} from './parseMeta';
import type { Complexity } from './types';

const PLUGIN_NAME = 'DemoRoutesPlugin';

// Demo 路由配置接口
export interface DemoRouteConfig {
  scanPaths?: string[];
  exclude?: string[];
  entryPath?: string;
  enabled?: boolean;
  outputPath?: string;
}

// 固定分类集合
const VALID_CATEGORIES = ['核心', '业务', '其他', '表单', '表格'] as const;

// Demo 元数据接口
interface DemoMeta {
  title: string;
  description?: string;
  category?: string[];
  author?: string;
  date?: string;
  path: string;
  componentPath: string;
  filePath: string;
  // ── 新增字段 ──
  aiTags?: string[];
  complexity?: Complexity;
  useCase?: string[];
  sourceComponent?: string;
  reusable?: boolean;
  lastVerified?: string;
  routeParams?: string;
}

/**
 * 从源代码中提取 JSDoc 注释
 */
function extractJSDoc(sourceCode: string): string | null {
  const jsdocRegex = /\/\*\*([\s\S]*?)\*\//;
  const match = sourceCode.match(jsdocRegex);
  return match ? match[1].trim() : null;
}

/**
 * 解析 JSDoc 标签
 */
function parseJSDocTags(jsdocContent: string): Record<string, string> {
  const tags: Record<string, string> = {};
  const lines = jsdocContent.split('\n');

  for (const line of lines) {
    const cleanedLine = line.replace(/^\s*\*\s?/, '').trim();
    const tagMatch = cleanedLine.match(/^@(\w+)\s*(.*)$/);
    if (tagMatch) {
      const [, tagName, tagValue] = tagMatch;
      tags[tagName] = tagValue.trim();
    }
  }

  return tags;
}

/**
 * 从文件路径生成路由路径
 */
function generateRoutePath(filePath: string): string {
  // 统一使用正斜杠处理路径（兼容 Windows 和 Unix）
  const normalizedPath = filePath.replace(/\\/g, '/');
  const demoIndex = normalizedPath.indexOf('$demo/');
  if (demoIndex === -1) return '';

  let relativePath = normalizedPath.slice(demoIndex + 6);
  // 处理边缘情况：$demo/index.tsx（没有子目录时，回退到 $demo 的父目录名）
  if (relativePath === 'index.tsx' || relativePath === 'index.ts') {
    const beforeDemo = normalizedPath.slice(0, demoIndex);
    const parts = beforeDemo.split('/').filter(Boolean);
    relativePath = parts[parts.length - 1] || 'unnamed-demo';
  } else {
    relativePath = relativePath.replace(/\/index\.(tsx|ts)$/, '');
  }
  relativePath = relativePath
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');

  return `/demos/${relativePath}`;
}

/**
 * 从文件路径生成默认标题
 */
function generateDefaultTitle(filePath: string): string {
  const match = filePath.match(/\$demo[/\\]([^/\\]+)/);
  if (!match) return 'Untitled Demo';

  return match[1]
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * 解析逗号分隔的分类字符串为数组
 */
function parseCategoryString(value: string): string[] {
  const result = value
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set(result)];
}

/**
 * 从文件路径生成分类（多分类数组）
 */
function generateCategory(filePath: string): string[] {
  if (filePath.includes('containers/')) return ['业务'];
  if (filePath.includes('components/SForm/')) return ['核心', '表单'];
  if (/Table|TableForm|ProTable/.test(filePath)) return ['表格'];
  if (filePath.includes('components/')) return ['其他'];
  return ['其他'];
}

/**
 * 解析 Demo 元数据
 */
function parseDemoMeta(sourceCode: string, filePath: string, context: string): DemoMeta {
  const jsdocContent = extractJSDoc(sourceCode);
  const tags = jsdocContent ? parseJSDocTags(jsdocContent) : {};
  const routePath = generateRoutePath(filePath);

  // 计算相对路径（从项目根目录到 $demo 目录的路径）
  const relativePath = path.relative(context, filePath).replace(/\\/g, '/');
  // 提取 $demo 原本的路径（相对于 src 或 features）
  const demoMatch = relativePath.match(/(\$demo\/.*)/);
  const filePathDisplay = demoMatch ? demoMatch[1] : relativePath;

  // 解析分类
  const category = tags.category
    ? parseCategoryString(tags.category)
    : generateCategory(filePath);

  // 校验分类值
  for (const cat of category) {
    if (!(VALID_CATEGORIES as readonly string[]).includes(cat)) {
      // console.warn(
      //   `[DemoRoutesPlugin] 未知分类 "${cat}" (文件: ${filePath})，有效分类: ${VALID_CATEGORIES.join(', ')}`
      // );
    }
  }

  // ── 解析 aiTags（复用 parseMeta.ts 共享函数。兼容 @aiTags 和 @aitags 大小写）──
  const tagsAiTags = tags.aitags || tags.aiTags;
  const aiTags = tagsAiTags
    ? parseTagsString(tagsAiTags)
    : undefined;
  if (aiTags && aiTags.length > 0) {
    validateAiTags(aiTags, filePath);
  }

  // ── 解析 complexity ──
  const complexity = tags.complexity
    ? validateComplexity(tags.complexity)
    : undefined;

  // ── 解析 useCase（兼容 @useCase 和 @usecase 大小写）──
  const tagsUseCase = tags.usecase || tags.useCase;
  const useCase = tagsUseCase
    ? parseUseCaseString(tagsUseCase)
    : undefined;

  // ── 解析 sourceComponent（兼容 @sourceComponent 和 @sourcecomponent 大小写）──
  const sourceComponent = tags.sourcecomponent || tags.sourceComponent || undefined;

  // ── 解析 reusable ──
  const reusable = tags.reusable
    ? tags.reusable.toLowerCase() === 'true'
    : undefined;

  // ── 解析 lastVerified（兼容 @lastVerified 和 @lastverified 大小写）──
  const lastVerified = tags.lastverified || tags.lastVerified || undefined;

  // ── 解析 routeParams（动态路由参数，如 ":tabkey"）──
  const routeParams = tags.routeparams || tags.routeParams || undefined;

  return {
    title: tags.title || generateDefaultTitle(filePath),
    description: tags.description || '',
    category,
    author: tags.author || '',
    date: tags.date || new Date().toISOString().split('T')[0],
    path: routePath,
    componentPath: filePath,
    filePath: filePathDisplay,
    // 新增字段
    aiTags,
    complexity,
    useCase,
    sourceComponent,
    reusable,
    lastVerified,
    routeParams,
  };
}

/**
 * Demo Routes Rspack 插件
 */
export class DemoRoutesPlugin {
  private config: Required<DemoRouteConfig>;
  private demoRoutes: DemoMeta[] = [];
  private context = '';
  private isFirstRun = true;

  constructor(config: DemoRouteConfig = {}) {
    this.config = {
      scanPaths: config.scanPaths || ['src/containers/**/$demo', 'src/components/**/$demo', 'src/$demo'],
      exclude: config.exclude || ['**/node_modules/**'],
      entryPath: config.entryPath || '/demos',
      enabled: config.enabled !== undefined ? config.enabled : process.env.NODE_ENV === 'development',
      outputPath: config.outputPath || 'src/demo-routes.generated.ts',
    };
  }

  apply(compiler: any) {
    this.context = compiler.options.context || process.cwd();

    if (!this.config.enabled) {
      // 生产模式：生成空文件
      this.generateEmptyFile();
      return;
    }

    // 开发模式：扫描并生成文件
    this.scanDemoFiles();
    this.generateRoutesFile();
    this.setupWatch(compiler);
  }

  private scanDemoFiles() {
    this.demoRoutes = [];
    const scannedPaths = new Set<string>();

    for (const scanPath of this.config.scanPaths) {
      // 处理两种模式：
      // 1. src/**/$demo - 递归查找所有 $demo 目录
      // 2. src/$demo - 直接扫描指定的 $demo 目录
      if (scanPath.includes('/**/$demo')) {
        // 模式1：递归查找
        const basePath = scanPath.replace(/\/\*\*\/\$demo$/, '');
        const fullPath = path.resolve(this.context, basePath);

        if (fs.existsSync(fullPath) && !scannedPaths.has(fullPath)) {
          scannedPaths.add(fullPath);
          this.scanDirectory(fullPath);
        }
      } else if (scanPath.endsWith('/$demo')) {
        // 模式2：直接扫描 $demo 目录
        const fullPath = path.resolve(this.context, scanPath);
        if (fs.existsSync(fullPath) && !scannedPaths.has(fullPath)) {
          scannedPaths.add(fullPath);
          this.scanDemoDirectory(fullPath);
        }
      }
    }

    this.demoRoutes.sort((a, b) => a.path.localeCompare(b.path));

    if (this.isFirstRun) {
      console.log(`[${PLUGIN_NAME}] 扫描到 ${this.demoRoutes.length} 个 Demo`);
      this.isFirstRun = false;
    }
  }

  private scanDirectory(dir: string) {
    try {
      const items = fs.readdirSync(dir);

      for (const item of items) {
        if (item.startsWith('.') || item === 'node_modules') continue;

        const fullPath = path.join(dir, item);
        const stat = fs.statSync(fullPath);

        if (stat.isDirectory()) {
          if (item === '$demo') {
            this.scanDemoDirectory(fullPath);
          } else {
            this.scanDirectory(fullPath);
          }
        }
      }
    } catch (error) {
      // 忽略
    }
  }

  private scanDemoDirectory(demoDir: string) {
    try {
      const files = fs.readdirSync(demoDir);

      for (const file of files) {
        if (file.startsWith('.')) continue;

        const fullPath = path.join(demoDir, file);
        const stat = fs.statSync(fullPath);

        if (stat.isDirectory()) {
          const indexPath = path.join(fullPath, 'index.tsx');
          const indexTsPath = path.join(fullPath, 'index.ts');

          if (fs.existsSync(indexPath)) {
            this.processDemoFile(indexPath);
          } else if (fs.existsSync(indexTsPath)) {
            this.processDemoFile(indexTsPath);
          }
        } else if (file.endsWith('.tsx')) {
          this.processDemoFile(fullPath);
        }
      }
    } catch (error) {
      // 忽略
    }
  }

  private processDemoFile(filePath: string) {
    try {
      const sourceCode = fs.readFileSync(filePath, 'utf-8');
      const meta = parseDemoMeta(sourceCode, filePath, this.context);

      if (!meta.title || meta.title.trim() === '') {
        return;
      }

      const relativePath = path.relative(this.context, filePath).replace(/\\/g, '/');

      this.demoRoutes.push({
        ...meta,
        componentPath: relativePath,
      });
    } catch (error) {
      console.warn(`[${PLUGIN_NAME}] 解析文件失败: ${filePath}`, error);
    }
  }

  private generateRoutesCode(): string {
    const imports: string[] = [];
    const routes: string[] = [];

    this.demoRoutes.forEach((route, index) => {
      // 生成导入路径
      let importPath: string;
      if (route.componentPath.includes('features/mpc-common/src/')) {
        // mpc-common 的组件使用别名导入
        importPath = route.componentPath.replace(/.*features\/mpc-common\/src\//, '@mpc/common/src/');
      } else {
        // 应用内的组件使用相对路径
        importPath = route.componentPath.replace(/^src\//, './');
      }
      // 移除 .tsx 或 .ts 扩展名
      importPath = importPath.replace(/\.(tsx|ts)$/, '');
      imports.push(`const Demo${index} = () => import(/* webpackChunkName: "demo-${index}" */ '${importPath}');`);

      routes.push(`  {
    path: '${route.path}',
    title: '${route.title.replace(/'/g, "\\'")}',
    description: '${(route.description || '').replace(/'/g, "\\'")}',
    category: ${JSON.stringify(route.category || [])},
    author: '${(route.author || '').replace(/'/g, "\\'")}',
    date: '${route.date || ''}',
    filePath: '${(route.filePath || '').replace(/'/g, "\\'")}',
    aiTags: ${JSON.stringify(route.aiTags || null)},
    complexity: ${route.complexity ? `'${route.complexity}'` : 'null'},
    sourceComponent: ${route.sourceComponent ? `'${route.sourceComponent.replace(/'/g, "\\'")}'` : 'null'},
    routeParams: ${route.routeParams ? `'${route.routeParams}'` : 'null'},
    component: Demo${index},
  }`);
    });

    return `// 此文件由 DemoRoutesPlugin 自动生成，请勿手动修改

${imports.join('\n')}

export const demoRoutes = [
${routes.join(',\n')}
];

export const isDemoMode = true;
`;
  }

  private generateEmptyCode(): string {
    return `// 此文件由 DemoRoutesPlugin 自动生成，请勿手动修改
// Demo 模式在生产环境已禁用

export const demoRoutes: any[] = [];
export const isDemoMode = false;
`;
  }

  private generateRoutesFile() {
    const code = this.generateRoutesCode();
    const outputPath = path.join(this.context, this.config.outputPath);
    
    // 确保目录存在
    const dir = path.dirname(outputPath);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }

    // 内容无变化时跳过写入，避免触发不必要的重新编译
    if (fs.existsSync(outputPath)) {
      const existing = fs.readFileSync(outputPath, 'utf-8');
      if (existing === code) {
        return;
      }
    }
    
    fs.writeFileSync(outputPath, code);
    console.log(`[${PLUGIN_NAME}] 生成路由文件: ${this.config.outputPath}`);
  }

  private generateEmptyFile() {
    const code = this.generateEmptyCode();
    const outputPath = path.join(this.context, this.config.outputPath);
    
    const dir = path.dirname(outputPath);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }

    // 内容无变化时跳过写入
    if (fs.existsSync(outputPath)) {
      const existing = fs.readFileSync(outputPath, 'utf-8');
      if (existing === code) {
        return;
      }
    }
    
    fs.writeFileSync(outputPath, code);
  }

  private setupWatch(compiler: any) {
    compiler.hooks.beforeCompile.tap(PLUGIN_NAME, () => {
      this.checkForChanges();
    });

    compiler.hooks.watchRun.tap(PLUGIN_NAME, () => {
      this.checkForChanges();
    });
  }

  private checkForChanges() {
    this.scanDemoFiles();
    // generateRoutesFile 内部会比较文件内容，无变化自动跳过
    this.generateRoutesFile();
  }
}

export default DemoRoutesPlugin;
